import { apiClient } from '_app'
import { CancelToken, CancelTokenStatic } from 'axios'
import { ApiResponse } from 'dtos'
import {
  BuyerExporterCompanyDto,
  OutboundTripDetailDestinationDto,
  OutboundTripDetailDto,
  OutboundTripDetailNotesDto,
  OutboundTripDetailPhotosDto,
  OutboundTripDetailVehicleDto,
  OutboundTripDto,
  OutboundTripListDto,
  OutboundTripQueryParams
} from 'dtos/outboundTrips'
import { download, objectToFormData, openInNewTab, URIQuery } from 'utils'

const baseUrl = (): string => '/api/outbound-trips'
const guidUrl = (outboundTripGuid: string): string => `${baseUrl()}/${encodeURIComponent(outboundTripGuid)}`

export const getOutboundTrips = (queryParams: OutboundTripQueryParams, cancelToken?: CancelToken) =>
  apiClient.get<ApiResponse<OutboundTripListDto[]>>(baseUrl() + URIQuery(queryParams), { cancelToken }).then(res => res.data)

export const getOutboundTripDetail = (outboundTripGuid: string, cancelToken?: CancelToken) =>
  apiClient.get<ApiResponse<OutboundTripDetailDto>>(`${guidUrl(outboundTripGuid)}/details`, { cancelToken }).then(res => res.data)

export const createOutboundTrip = (data: OutboundTripDto, cancelToken?: CancelToken) =>
  apiClient.post<ApiResponse<OutboundTripDto>>(baseUrl(), data, { cancelToken }).then(res => res.data)

export const updateOutboundTrip = (data: OutboundTripDto, cancelToken?: CancelToken) =>
  apiClient.put<ApiResponse<OutboundTripDto>>(baseUrl(), data, { cancelToken }).then(res => res.data)

export const deleteOutboundTrip = (outboundTripGuid: string, cancelToken?: CancelToken) =>
  apiClient.delete<undefined>(guidUrl(outboundTripGuid), { cancelToken }).then(res => res.data)

export const generateBillOfLading = (outboundTripGuid: string, dest: 'download' | 'browser' = 'browser', cancelToken?: CancelToken) =>
  apiClient.get<string>(`${guidUrl(outboundTripGuid)}/generate-bill-of-lading`, { cancelToken }).then(res => {
    if (dest === 'download') {
      download(res.data, 'OutboundBillOfLading.pdf', 'application/pdf')
    } else if (dest === 'browser') {
      openInNewTab(res.data, 'application/pdf')
    }
  })

export const lock = (outboundTripGuid: string, cancelToken?: CancelToken) =>
  apiClient.put<ApiResponse<OutboundTripDto>>(`${guidUrl(outboundTripGuid)}/lock`, { cancelToken }).then(res => res.data)

export const unlock = (outboundTripGuid: string, cancelToken?: CancelToken) =>
  apiClient.put<ApiResponse<OutboundTripDto>>(`${guidUrl(outboundTripGuid)}/unlock`, { cancelToken }).then(res => res.data)

export const getSignedOutboundBillOfLading = (guid: string) =>
  apiClient.get(`${guidUrl(guid)}/signed-outbound-bill-of-lading`).then(res => openInNewTab(res.data, 'application/pdf'))

export const getVehicles = (cancelToken?: CancelToken) =>
  apiClient.get<ApiResponse<OutboundTripDetailVehicleDto[]>>(`${baseUrl()}/vehicles`, { cancelToken }).then(res => res.data)

export const addVehicles = (outboundTripGuid: string, data: OutboundTripDetailVehicleDto[], cancelToken?: CancelToken) =>
  apiClient.post<ApiResponse<OutboundTripDetailVehicleDto[]>>(`${guidUrl(outboundTripGuid)}/vehicles`, data, { cancelToken }).then(res => res.data)

export const removeVehicles = (outboundTripGuid: string, data: OutboundTripDetailVehicleDto[], cancelToken?: CancelToken) =>
  apiClient.delete<ApiResponse<OutboundTripDetailVehicleDto[]>>(`${guidUrl(outboundTripGuid)}/vehicles`, { data, cancelToken }).then(res => res.data)

export const saveDestination = (outboundTripGuid: string, data: OutboundTripDetailDestinationDto, cancelToken?: CancelToken) =>
  apiClient.put<ApiResponse<OutboundTripDetailDestinationDto>>(`${guidUrl(outboundTripGuid)}/destination`, data, { cancelToken }).then(res => res.data)

export const saveNotes = (outboundTripGuid: string, data: OutboundTripDetailNotesDto, cancelToken?: CancelToken) =>
  apiClient.put<ApiResponse<OutboundTripDetailNotesDto>>(`${guidUrl(outboundTripGuid)}/notes`, data, { cancelToken }).then(res => res.data)

export const savePhotos = (outboundTripGuid: string, data: OutboundTripDetailPhotosDto, cancelToken?: CancelToken) =>
  apiClient<ApiResponse<OutboundTripDetailPhotosDto>>({
    data: objectToFormData(data),
    headers: { 'Content-Type': 'multipart/form-data' },
    method: 'put',
    url: `${guidUrl(outboundTripGuid)}/photos`
  }).then(res => res.data)

export const getBuyerExporterCompanies = (cancelToken?: CancelToken) =>
  apiClient<ApiResponse<BuyerExporterCompanyDto[]>>({
    method: 'get',
    url: `${baseUrl()}/buyer-exporter-companies`
  }).then(res => res.data)
